import React, { useState } from 'react';
import { PatternFormat } from 'react-number-format';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { requestPassword } from '../redux/AuthCRUD';

const initialValues = {
  email: '',
  isSMS: false,
  phone: {
    floatValue: '',
    formattedValue: '',
    value: '',
  },
};
const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Doğru bir email adresi giriniz.')
    .min(3, 'En az 3 karakter girilmelidir.')
    .max(50, 'En fazla 50 karakter girilebilir.')
    .when('isSMS', {
      is: false,
      then: Yup.string().required('Lütfen email adresini girin.'),
      otherwise: Yup.string().nullable(),
    }),
  isSMS: Yup.bool(),
  phone: Yup.object().shape({
    floatValue: Yup.string(),
    formattedValue: Yup.string(),
    value: Yup.string()
      .min(10, 'Telefon numarası 10 haneli girilmelidir.')
      .max(10, 'Telefon numarası 10 haneli girilmelidir.')
      .when('$isSMS', {
        is: true,
        then: Yup.string().required('Lütfen telefon numaranızı girin.'),
        otherwise: Yup.string().nullable(),
      }),
  }),
});

export function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined);
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      setHasErrors(undefined);
      setTimeout(() => {
        requestPassword(values)
          .then(() => {
            setHasErrors(false);
            setLoading(false);
          })
          .catch((error) => {
            setHasErrors(true);
            setLoading(false);
            setSubmitting(false);
            if (error.response && error.response.data && error.response.data.message) {
              setStatus(error.response.data.message);
              const errorMessage = error.response.data.message;
              if (errorMessage === 'CHECK_USER_INFO') {
                setStatus('E-posta adresi yanlış olabilir, girilen bilgileri kontrol ediniz.');
              } else if (errorMessage === 'USER_ALREADY_HAS_FORGOT_PASS_INFO') {
                setStatus('Parola sıfırlama talebi 24 saat içerisinde sadece 3 kez gönderilebilir. Parola yenileme e-postası elinize ulaşmadıysa spam klasörünü kontrol ediniz.');
              }
            } else {
              setStatus('Bir hata oluştu.');
            }
          });
      }, 1000);
    },
  });

  return (
    <>
      <form className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework" noValidate id="kt_login_password_reset_form" onSubmit={formik.handleSubmit}>
        <div className="text-center mb-10">
          {/* begin::Title */}
          <h1 className="text-dark mb-3">Parolanızı mı unuttunuz ?</h1>
          {/* end::Title */}

          {/* begin::Link */}
          <div className="text-gray-400 fw-bold fs-4">Parolanızı sıfırlamak için {formik.values.isSMS ? 'telefon numaranızı' : 'e-postanızı'} girin.</div>
          {/* end::Link */}
        </div>
        {/* begin::Title */}
        {hasErrors === true && (
          <div className="mb-10 alert alert-danger">
            <div className="alert-text font-weight-bold">{formik.status}</div>
          </div>
        )}
        {hasErrors === false && (
          <div className="mb-10 bg-light-info p-8 rounded">
            <div className="text-info">Parola sıfırlama gönderildi. Lütfen {formik.values.isSMS ? 'mesajlarınızı' : 'e-postanızı'} kontrol edin</div>
          </div>
        )}
        {/* end::Title */}
        {/* begin::Form group */}
        <div className="fv-row mb-10">
          <div className="d-flex align-items-center justify-content-center mt-3">
            <label className="form-check form-check-inline form-check-solid me-lg-5">
              <input
                className="form-check-input"
                name="isSMSn"
                type="radio"
                defaultChecked={!formik.values.isSMS}
                onChange={(e) => {
                  formik.setFieldValue('isSMS', false);
                }}
              />
              <span className="fw-bold ps-2 fs-6 cursor-pointer">E-Posta</span>
            </label>

            <label className="form-check form-check-custom form-check-solid cursor-pointer">
              <input
                className="form-check-input"
                name="isSMSn"
                type="radio"
                defaultChecked={formik.values.isSMS}
                onChange={(e) => {
                  formik.setFieldValue('isSMS', true);
                }}
              />
              <span className="fw-bold ps-2 fs-6">Telefon Numarası</span>
            </label>
          </div>
        </div>
        {formik.values.isSMS ? (
          <div className="fv-row mb-10">
            <PatternFormat
              value={formik.values.phone.formattedValue}
              format="(###) ### ## ##"
              placeholder="Telefon numaranız"
              onValueChange={(e) => formik.setFieldValue('phone', e)}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                { 'is-invalid border-danger': formik.touched.phone?.value && formik.errors.phone?.value },
                {
                  'is-valid': formik.touched.phone?.value && !formik.errors.phone?.value,
                },
              )}
            />
            {formik.touched.phone?.value && formik.errors.phone?.value && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">
                  <span role="alert" className="text-danger">
                    {formik.errors.phone?.value}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="fv-row mb-10">
            <input
              type="email"
              placeholder="Email adresiniz"
              autoComplete="off"
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                { 'is-invalid border-danger': formik.touched.email && formik.errors.email },
                {
                  'is-valid': formik.touched.email && !formik.errors.email,
                },
              )}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">
                  <span role="alert" className="text-danger">
                    {formik.errors.email}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        {/* end::Form group */}
        {/* begin::Form group */}
        <div className="d-flex flex-wrap justify-content-center pb-lg-0">
          <button type="submit" id="kt_password_reset_submit" className="btn btn-lg btn-primary fw-bolder me-4" disabled={loading}>
            <span className="indicator-label">Gönder</span>
            {loading && <span className="spinner-border spinner-border-sm align-middle ms-2"></span>}
          </button>
          <Link to="/auth/login">
            <button type="button" id="kt_login_password_reset_form_cancel_button" className="btn btn-lg btn-secondary" disabled={formik.isSubmitting || !formik.isValid}>
              İptal
            </button>
          </Link>{' '}
        </div>

        { hasErrors === false && (
          <div className="mt-5 fs-8 text-center lh-1">
            Şifre problemlerinizde idefix’e kayıtlı satıcı mail adresiniz üzerinden{' '}
            <a className="btn btn-link btn-sm" href={'mailto:destek.satici@idefix.com'}>
              “destek.satici@idefix.com”
            </a>{' '}
            adresine mail ileterek destek alabilirsiniz.
          </div>
        )}
        {/* end::Form group */}
      </form>
    </>
  );
}
